import React, { useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Box, CircularProgress, Typography } from "@mui/material";
import Button from "@components/Button/Button";
import ErrorHandler from "@components/ErrorHandler/ErrorHandler";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { PartyType, SequenceView } from "@services/drybulkCargoOperationsApi";
import { useTranslation } from "react-i18next";
import IntermediateSurveyDialog from "../closeOut/IntermediateSurveyDialog";
import ToleranceDialog from "../closeOut/ToleranceDialog";
import ToleranceHistoryPanel from "../closeOut/ToleranceHistoryPanel";
import { DRYBULK_CARGO_OPS_LANG_BASE, SEQUENCE_EDITABLE_STATUSES } from "../constants";
import { useCargoOperationDetail } from "../hooks/useCargoOperationDetail";
import { useIsCargoOpsShippingAgent } from "../hooks/useIsCargoOpsShippingAgent";
import { usePollingGate } from "../hooks/usePollingGate";
import { useRemoveSequence } from "../hooks/useRemoveSequence";
import type { DetailExtensions } from "./detailExtensions";
import PartyPanel from "./PartyPanel";
import RemoveSequenceDialog from "./RemoveSequenceDialog";
import SequencePanel from "./SequencePanel/SequencePanel";
import SequencesGrid from "./SequencesGrid";
import ShippersGrid, { type CloseOutKind } from "./ShippersGrid";
import StockPilesGrid from "./StockPilesGrid";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.sequences`;

type CloseOutState = { kind: CloseOutKind; code: string } | null;

type PanelState = { mode: "add" } | { mode: "update"; sequence: SequenceView } | null;

const SequenceSection: React.FC<{
  operationId: string;
  poll?: boolean;
  pausePolling?: boolean;
  extensions?: DetailExtensions;
  onOpenSequence?: (sequence: SequenceView) => void;
}> = ({ operationId, poll = true, pausePolling = false, extensions = {}, onOpenSequence }) => {
  const { t } = useTranslation();
  const [panel, setPanel] = useState<PanelState>(null);
  const [party, setParty] = useState<{ code: string; type: PartyType } | null>(null);
  const [closeOut, setCloseOut] = useState<CloseOutState>(null);
  const gate = usePollingGate();
  const { removing, request, cancel, confirm, pending } = useRemoveSequence(operationId);

  const paused =
    !poll ||
    pausePolling ||
    gate.busy ||
    panel !== null ||
    party !== null ||
    removing !== null ||
    closeOut !== null;
  const detail = useCargoOperationDetail(operationId, { paused });
  const isOperationsUser = useAuthStore((state) => state.isOperationsUser);
  const isShippingAgent = useIsCargoOpsShippingAgent();

  if (detail.isLoading) {
    return <CircularProgress aria-label={t(`${L}.title`)} />;
  }
  if (detail.isError || !detail.data) {
    return <ErrorHandler error={detail.error} testId="drybulk-sequence-section-load-error" />;
  }

  const { data } = detail;

  return (
    <Box data-testid="sequence-section" sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography variant="h6" component="h3">
        {t(`${L}.title`)}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SequencesGrid
          sequences={data.sequences ?? []}
          operationStatus={data.status}
          readOnly={!isShippingAgent}
          onUpdate={(sequence) => setPanel({ mode: "update", sequence })}
          onRemove={request}
          onOpen={
            onOpenSequence && (isOperationsUser || isShippingAgent) ? onOpenSequence : undefined
          }
          openInMenu={isOperationsUser && Boolean(onOpenSequence)}
          rowActions={extensions.rowActions}
          extraColumns={extensions.extraColumns}
        />
        {isShippingAgent && SEQUENCE_EDITABLE_STATUSES.includes(data.status ?? "") && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setPanel({ mode: "add" })}
              label={<span className="normal-case font-medium">{t(`${L}.add`)}</span>}
            />
          </Box>
        )}
      </Box>
      <ShippersGrid
        shippers={data.shippers ?? []}
        operationId={operationId}
        operationStatus={data.status}
        onOpen={(code) => setParty({ code, type: "SHIPPER" })}
        onCloseOut={(kind, code) => setCloseOut({ kind, code })}
      />
      <StockPilesGrid
        stockPiles={data.stockPiles ?? []}
        onOpen={(code) => setParty({ code, type: "STOCK_PILE" })}
      />

      {extensions.executionSection}
      {extensions.closeOutSection}

      {panel && (
        <SequencePanel
          key={panel.mode === "update" ? panel.sequence.id : "add"}
          operationId={operationId}
          sequence={panel.mode === "update" ? panel.sequence : null}
          onClose={() => setPanel(null)}
        />
      )}
      {party && (
        <PartyPanel
          operationId={operationId}
          code={party.code}
          type={party.type}
          onClose={() => setParty(null)}
        />
      )}
      {closeOut?.kind === "intermediate" && (
        <IntermediateSurveyDialog
          operationId={operationId}
          shipperCode={closeOut.code}
          onClose={() => setCloseOut(null)}
        />
      )}
      {closeOut?.kind === "tolerance" && (
        <ToleranceDialog
          operationId={operationId}
          shipperCode={closeOut.code}
          tolerancePct={
            data.shippers?.find((row) => row.shipperCompanyCode === closeOut.code)?.tolerancePct ??
            0
          }
          onClose={() => setCloseOut(null)}
        />
      )}
      {closeOut?.kind === "history" && (
        <ToleranceHistoryPanel
          operationId={operationId}
          shipperCode={closeOut.code}
          onClose={() => setCloseOut(null)}
        />
      )}
      <RemoveSequenceDialog
        sequence={removing}
        isSubmitting={pending}
        onConfirm={() => void confirm()}
        onCancel={cancel}
      />
    </Box>
  );
};

export default SequenceSection;
